import { SpinnerGap, PlayCircle, DownloadSimple, Trash } from "@phosphor-icons/react";

// The Events list rows on Game Detail — extracted verbatim (no behavior
// change). Kept as one component since every row shares the same handlers.
export function EventsList({
  events, eventsLoading, game, isViewer, EVENT_TYPE_LABELS, EVENT_TYPE_BADGE_CLS,
  formatEventTime, eventWhoLabel, playerLabel, downloadingClipId, clipDownloadStage,
  savingEventId, onJumpToEvent, onDownloadClip, onConfirmEvent, onEditEvent, onDeleteEvent,
}) {
  const hasVideo = !!(game.video_url || game.video_upload_id || game.bunny_video_id);

  if (eventsLoading && events.length === 0) {
    return <p className="text-sm text-zinc-500 flex items-center gap-2" data-testid="events-loading"><SpinnerGap size={16} className="animate-spin" /> Loading events…</p>;
  }
  if (events.length === 0) {
    return <p className="text-sm text-zinc-500" data-testid="events-empty">No events logged yet.</p>;
  }

  return (
    <div className="space-y-2">
      {events.map((ev) => (
        <div key={ev.id} data-testid={`event-row-${ev.id}`} className="flex items-center gap-3 flex-wrap bg-[#0A0A0A] border border-zinc-800 rounded-sm px-4 py-3">
          <span className="font-label text-xs text-zinc-500 w-12 shrink-0" data-testid={`event-time-${ev.id}`}>{formatEventTime(ev.timestamp)}</span>
          <span className={"font-label text-[10px] uppercase px-2 py-1 rounded-sm shrink-0 " + EVENT_TYPE_BADGE_CLS[ev.type]}>
            {EVENT_TYPE_LABELS[ev.type]}
          </span>
          <span className={"font-label text-[10px] uppercase px-2 py-1 rounded-sm shrink-0 " + (ev.team === "our_team" ? "bg-zinc-700/40 text-zinc-200" : "bg-orange-500/15 text-orange-400")}>
            {ev.team === "our_team" ? "Us" : "Opposing"}
          </span>
          <div className="flex-1 min-w-[140px]">
            <p className="text-sm text-white" data-testid={`event-player-${ev.id}`}>
              {eventWhoLabel(ev)}
              {ev.type === "goal" && ev.assist_team && (
                <span className="text-zinc-500" data-testid={`event-assist-${ev.id}`}>
                  {" — assist: "}{ev.assist_team === "our_team" ? (ev.assist_player_id ? playerLabel(ev.assist_player_id) : "Player unknown") : "Opposing team"}
                </span>
              )}
            </p>
            {ev.outcome && <p className="text-xs text-zinc-500 capitalize">{ev.outcome.replace("_", " ")}</p>}
            {ev.type === "save" && ev.linked_shot_event_id && (() => {
              const shot = events.find((e) => e.id === ev.linked_shot_event_id);
              return shot ? <p className="text-xs text-zinc-500" data-testid={`event-linked-shot-${ev.id}`}>Linked to shot at {formatEventTime(shot.timestamp)}</p> : null;
            })()}
          </div>
          <span
            data-testid={`event-source-${ev.id}`}
            className={"font-label text-[10px] uppercase px-2 py-1 rounded-sm shrink-0 " + (ev.source === "ai" ? "bg-purple-500/15 text-purple-300" : "bg-zinc-700/40 text-zinc-300")}
          >
            {ev.source === "ai" ? `AI · ${Math.round((ev.confidence || 0) * 100)}%` : "Manual"}
          </span>
          {ev.status === "pending" && (
            <span data-testid={`event-status-${ev.id}`} className="font-label text-[10px] uppercase px-2 py-1 rounded-sm bg-amber-500/15 text-amber-400 shrink-0">Pending review</span>
          )}
          {hasVideo && (
            <div className="flex items-center gap-3 shrink-0">
              <button type="button" onClick={() => onJumpToEvent(ev)} data-testid={`event-jump-${ev.id}`} className="flex items-center gap-1 text-[#CCFF00] hover:text-[#B3E600] transition-colors duration-150 font-label text-xs">
                <PlayCircle size={14} weight="fill" /> Jump to clip
              </button>
              {!isViewer && (
                <button type="button" onClick={() => onDownloadClip(ev)} disabled={downloadingClipId === ev.id} data-testid={`event-download-${ev.id}`} className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors duration-150 font-label text-xs disabled:opacity-50">
                  {downloadingClipId === ev.id ? <SpinnerGap size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />} {downloadingClipId === ev.id ? clipDownloadStage : "Download"}
                </button>
              )}
            </div>
          )}
          {!isViewer && (
            <div className="flex items-center gap-2 shrink-0">
              {ev.status === "pending" && (
                <button type="button" onClick={() => onConfirmEvent(ev)} disabled={savingEventId === ev.id} data-testid={`event-confirm-${ev.id}`} className="font-label text-[10px] uppercase px-2 py-1.5 rounded-sm bg-[#CCFF00]/15 text-[#CCFF00] hover:bg-[#CCFF00]/25 transition-colors duration-150 disabled:opacity-50">
                  Confirm
                </button>
              )}
              <button type="button" onClick={() => onEditEvent(ev)} data-testid={`event-edit-${ev.id}`} className="font-label text-[10px] uppercase px-2 py-1.5 rounded-sm bg-zinc-800 text-zinc-300 hover:text-white transition-colors duration-150">
                Edit
              </button>
              <button type="button" onClick={() => onDeleteEvent(ev)} disabled={savingEventId === ev.id} data-testid={`event-delete-${ev.id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 disabled:opacity-50">
                <Trash size={14} weight="bold" />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
